// Historically the single source of truth for turning a backend errorCode
// into user-facing copy for BOTH the text and image pipelines. As of the
// image pipeline's move to a presentationKey-based contract
// (config/imageProviderErrorPresentation.js, consumed directly by
// BattleImage.js), that's no longer true for images — BattleImage.js does
// not import this file at all, so this map's title/detail fields for
// moderation/moderation_output_blocked/generation_failed/
// auth_required_for_battle are legacy copy that is no longer rendered
// anywhere in production. This file's live, current-production usage is
// narrower than it looks:
//  - getErrorMessage(...).rulesPath is still consulted, but only via
//    resolveRulesPath.js, which itself is only called by the TEXT battle
//    pipeline (hooks/useBattleRequest.js) — see resolveRulesPath.js's own
//    header for that history.
//  - moderation_blocked is the text pipeline's own code for the user's
//    literal typed input; it's the one entry below still backing a rendered
//    error banner today.
// The three-way moderation_blocked / moderation_output_blocked / moderation
// distinction below was originally written for the image pipeline (which no
// longer emits moderation_output_blocked/moderation copy from this file);
// kept for now since deleting the dead entries is a source-code change, not
// a documentation one — flagged as a cleanup candidate for a future phase.
// Shared with AccountStandingSection.js's "View Rules" link — one definition
// for the anchor RulesPage.js's "How Enforcement Works" section actually uses.
export const DEFAULT_ENFORCEMENT_RULES_PATH = "/rules#how-enforcement-works";

const ERROR_MESSAGES = {
  moderation_blocked: {
    title: "Image request blocked",
    detail: "This request wasn't allowed under our Rules & Code of Conduct.",
    // Fallback only — an image block usually carries a real, per-request
    // `policy` from the server (a specific rule when known, or the generic
    // enforcement explainer when not — see who-would-win-server's
    // errors/moderation.js), which callers should prefer over this. This
    // static value only applies when no dynamic policy is available at all
    // (e.g. a network-level failure with no backend response).
    rulesPath: DEFAULT_ENFORCEMENT_RULES_PATH,
  },
  moderation_output_blocked: {
    title: "Image couldn't be displayed",
    detail: "The generated image didn't meet our safety requirements, so it was withheld. This wasn't necessarily caused by your request.",
    rulesPath: DEFAULT_ENFORCEMENT_RULES_PATH,
  },
  moderation: {
    title: "Image generation blocked for safety reasons",
    detail: "This image couldn't be generated because the request triggered a safety restriction. Review our Rules & Code of Conduct before trying a different request.",
    rulesPath: DEFAULT_ENFORCEMENT_RULES_PATH,
  },
  rate_limited: {
    title: "Too many requests",
    detail: "You're generating images too quickly — please wait a moment and try again.",
  },
  generation_failed: {
    title: "Couldn't generate image.",
    detail: "Something went wrong. Try regenerating below.",
  },
  auth_required_for_battle: {
    title: "Sign in to regenerate this image",
    detail: "Create a free account or sign in, then try again.",
  },
};

export function getErrorMessage(errorCode) {
  return ERROR_MESSAGES[errorCode] ?? ERROR_MESSAGES.generation_failed;
}
